// Utility functions for pricing and status lookups
import { getClientStatuses, getEstimateStatuses, getOperationsStatuses, DEFAULT_ESTIMATE_STATUSES } from '../lib/constants';

const DEFAULT_RATES = [
    { crew_size: 2, truck_type: 'none', service_type: 'moving', hourly_rate: 200 },
    { crew_size: 2, truck_type: '16ft', service_type: 'moving', hourly_rate: 185 },
    { crew_size: 2, truck_type: '26ft', service_type: 'moving', hourly_rate: 200 },
    { crew_size: 3, truck_type: '26ft', service_type: 'moving', hourly_rate: 235 },
    { crew_size: 4, truck_type: '26ft', service_type: 'moving', hourly_rate: 275 },
    // 2026-09-11: was 6 / '2x26ft' @ 350. Two trucks are now primary + extra
    // (surcharge on top), so this is the 6-crew single-26' base. Jaylen to
    // confirm in Settings → Rate Table; until saved these defaults rule.
    { crew_size: 6, truck_type: '26ft', service_type: 'moving', hourly_rate: 350 },
    { crew_size: 2, truck_type: 'none', service_type: 'packing', hourly_rate: 125 },
];

export function lookupRate(config, crewSize, truckType, serviceType = 'moving') {
    // Looks up hourly rate from the rate table (array of {crew_size, truck_type, service_type, hourly_rate})
    // truckType: 'none', '16ft', '20ft', '26ft' — or legacy truckIds array (auto-converted).
    // A legacy '2x26ft' (retired 2026-09-11) prices as '26ft'; the second
    // truck is an extra with the per-hour surcharge, not a rate row.
    // Returns hourly rate in dollars, or 0 if no match

    try {
        let rateTable = [];
        try {
            const raw = config?.rate_table;
            // Handle both string (from DB) and already-parsed array (from Supabase JSONB)
            if (Array.isArray(raw)) {
                rateTable = raw;
            } else if (typeof raw === 'string' && raw.length > 2) {
                rateTable = JSON.parse(raw);
            }
        } catch { rateTable = []; }
        if (!Array.isArray(rateTable) || rateTable.length === 0) rateTable = DEFAULT_RATES;

        // Normalize crewSize to number for comparison
        const crew = parseInt(crewSize) || 0;

        // Legacy support: if truckType is an array (old truckIds format), convert to truck_type string
        let resolvedTruckType = truckType;
        if (Array.isArray(truckType)) {
            resolvedTruckType = truckType.length === 0 ? 'none' : '26ft';
        }
        if (!resolvedTruckType) resolvedTruckType = 'none';
        if (resolvedTruckType === '2x26ft') resolvedTruckType = '26ft';

        // Find exact match: crew_size + truck_type + service_type (use == for crew_size to handle string/number mismatch)
        const exact = rateTable.find(r =>
            parseInt(r.crew_size) === crew &&
            r.truck_type === resolvedTruckType &&
            r.service_type === serviceType
        );
        if (exact) return parseFloat(exact.hourly_rate) || 0;

        // Fallback: match crew_size + service_type (any truck)
        const crewMatch = rateTable.find(r =>
            parseInt(r.crew_size) === crew &&
            r.service_type === serviceType
        );
        if (crewMatch) return parseFloat(crewMatch.hourly_rate) || 0;

        console.warn('lookupRate: no match for crew=' + crew + ' truck=' + resolvedTruckType + ' service=' + serviceType, 'table has', rateTable.length, 'rows');
        return 0;
    } catch (e) {
        console.warn('Rate lookup failed:', e);
        return 0;
    }
}

// Same lookup, but says HOW it matched (2026-09-11, Billing Calculator):
// { rate, exact, fallbackTruck }. `exact` = a row for this crew + truck
// exists; otherwise `fallbackTruck` names the row it borrowed (same crew,
// any truck) so surfaces can flag "no 2x26' row — using the 26' rate"
// instead of presenting a borrowed number as the table's answer.
export function lookupRateInfo(config, crewSize, truckType, serviceType = 'moving') {
    let rateTable = [];
    try {
        const raw = config?.rate_table;
        if (Array.isArray(raw)) rateTable = raw;
        else if (typeof raw === 'string' && raw.length > 2) rateTable = JSON.parse(raw);
    } catch { rateTable = []; }
    if (!Array.isArray(rateTable) || rateTable.length === 0) rateTable = DEFAULT_RATES;
    const crew = parseInt(crewSize) || 0;
    const truck = (truckType === '2x26ft' ? '26ft' : truckType) || 'none';
    const exact = rateTable.find(r => parseInt(r.crew_size) === crew && r.truck_type === truck && r.service_type === serviceType);
    if (exact) return { rate: parseFloat(exact.hourly_rate) || 0, exact: true, fallbackTruck: null, usingDefaults: rateTable === DEFAULT_RATES };
    const crewMatch = rateTable.find(r => parseInt(r.crew_size) === crew && r.service_type === serviceType);
    if (crewMatch) return { rate: parseFloat(crewMatch.hourly_rate) || 0, exact: false, fallbackTruck: crewMatch.truck_type, usingDefaults: rateTable === DEFAULT_RATES };
    return { rate: 0, exact: false, fallbackTruck: null, usingDefaults: rateTable === DEFAULT_RATES };
}

export function lookupPackingRate(config, crewSize) {
    return lookupRate(config, crewSize || 2, 'none', 'packing');
}

// Legacy → current key remap. Pre-rewrite data (and the old draft/sent/
// accepted flow) still renders with the correct current chip.
//   new → building, lost → cancelled  (May 2026 5→8 status rewrite)
//   draft → building, sent → quoted, accepted → booked  (older flow)
const LEGACY_ESTIMATE_STATUS_KEYS = {
    new: 'building',
    lost: 'cancelled',
    draft: 'building',
    sent: 'quoted',
    accepted: 'booked',
};

export function getEstimateStatus(statusKey) {
    // Returns {key, label, color, bg} for an estimate status.
    //
    // Reads from DEFAULT_ESTIMATE_STATUSES (lib/constants.js) — the single
    // source of truth — so this can't drift out of sync with the pipeline
    // the way the old hardcoded map did when statuses were rewritten 5→8.
    // Legacy keys are remapped to their current equivalent first.
    const resolvedKey = LEGACY_ESTIMATE_STATUS_KEYS[statusKey] || statusKey;
    const match = DEFAULT_ESTIMATE_STATUSES.find(s => s.key === resolvedKey);
    // Shallow-copy so callers can't mutate the shared constant.
    if (match) return { ...match };
    return { key: statusKey, label: statusKey, color: '#64748b', bg: '#f1f5f9' };
}

export { getClientStatuses, getEstimateStatuses, getOperationsStatuses } from '../lib/constants';
